import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

import { usePrefersReducedMotion } from "../../hooks/usePrefersReducedMotion.js";

/* ─────────────────────────────────────────────────────────────
   Magnetic — wraps interactive content (typically a button) and
   gives it a subtle pull toward the pointer on hover, like metal
   drawn to a magnet. Disabled entirely under reduced motion,
   rather than just skipping the animation on top of it.
───────────────────────────────────────────────────────────── */
export default function Magnetic({ children, className = "", strength = 14 }) {
  const ref = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 200, damping: 16, mass: 0.3 });
  const springY = useSpring(y, { stiffness: 200, damping: 16, mass: 0.3 });

  if (prefersReducedMotion) {
    return <div className={className}>{children}</div>;
  }

  function handlePointerMove(event) {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    x.set(((event.clientX - (rect.left + rect.width / 2)) / (rect.width / 2)) * strength);
    y.set(((event.clientY - (rect.top + rect.height / 2)) / (rect.height / 2)) * strength);
  }

  function handlePointerLeave() {
    x.set(0);
    y.set(0);
  }

  return (
    <motion.div
      ref={ref}
      className={className}
      style={{ x: springX, y: springY, display: "inline-block" }}
      onPointerMove={handlePointerMove}
      onPointerLeave={handlePointerLeave}
    >
      {children}
    </motion.div>
  );
}
